import Dexie, { type Table } from 'dexie';

export interface UndoRedoState {
  id?: number;
  type: 'undo' | 'redo';
  slides: any[];
  selectedIndex: number;
  themeId?: string;
  changeType: 'slide' | 'theme' | 'both';
  panelState?: string;
  timestamp: number;
}

export interface PresentationBackup {
  id: string;
  slides: any[];
  rosTitle: string;
  layoutMode: string;
  teamOwnerId?: string;
  timestamp: string;
  version: string;
  deletedSlides: string[];
}

class StreamAlive extends Dexie {
  presentations!: Table<PresentationBackup, string>;
  history!: Table<UndoRedoState, number>;

  constructor() {
    super('StreamAlive');
    this.version(2).stores({
      presentations: 'id, timestamp',
      history: '++id, type, timestamp'
    });
  }

  async clearHistory(): Promise<void> {
    await this.history.clear();
  }

  async clearAllPresentations(): Promise<void> {
    await this.presentations.clear();
  }

  async savePresentation(backup: PresentationBackup): Promise<void> {
    await this.presentations.put(backup);
  }

  async getPresentation(id: string): Promise<PresentationBackup | undefined> {
    return await this.presentations.get(id);
  }

  async deletePresentation(id: string): Promise<void> {
    await this.presentations.delete(id);
  }

  async pushHistory(
    type: 'undo' | 'redo',
    state: Omit<UndoRedoState, 'timestamp' | 'id' | 'type'>
  ): Promise<void> {
    await this.history.add({
      ...state,
      type,
      timestamp: Date.now()
    });
  }

  async getLatestHistory(type: 'undo' | 'redo'): Promise<UndoRedoState | undefined> {
    return await this.history
      .where('type')
      .equals(type)
      .reverse()
      .first();
  }

  async removeLatestHistory(type: 'undo' | 'redo'): Promise<UndoRedoState | undefined> {
    const latest = await this.getLatestHistory(type);
    if (latest && latest.id !== undefined) {
      await this.history.delete(latest.id);
    }
    return latest;
  }
}

export const db = new StreamAlive();
